import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Package, 
  Building2, 
  DollarSign, 
  Calendar, 
  FileText, 
  Hash, 
  Edit,
  Trash2,
  TrendingDown,
  Truck,
  User,
  Wrench,
  Clock,
  Tag,
  Info,
  MapPin,
  Briefcase,
  Upload,
  Image as ImageIcon,
  Download,
  Eye,
  X,
  ZoomIn,
  Plus
} from 'lucide-react';
import { exitsService } from '../services/exitsService';
import { productsService } from '../services/productsService';
import { imagesService }  from '../services/imagesService';
import { trucksService } from '../services/trucksService';
import ImageUploader from '../components/ImageUploader';
import ExitFormMulti from '../components/ExitFormMulti';
import MaintenanceModal from '../components/MaintenanceModal';

const ExitDetailPage = () => {
  const params = useParams();
  const id = params?.id;
  const navigate = useNavigate();
  const location = useLocation();

  const page = (() => {
    const p = new URLSearchParams(location.search).get('page');
    return p || sessionStorage.getItem('exits_page') || '1';
  })();
  
  const [exit, setExit] = useState({ products: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showDeliveryPhotosUploader, setShowDeliveryPhotosUploader] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [selectedDeliveryPhoto, setSelectedDeliveryPhoto] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(id === 'new');
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [truckData, setTruckData] = useState(null);
  const [loadingTruck, setLoadingTruck] = useState(false);

  // Función para formatear dinero
  const formatMoney = (amount) => {
    if (!amount) return '$0.00 MXN';
    
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return '$0.00 MXN';
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number) + ' MXN';
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'No especificada';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    // Ajustar por zona horaria para mostrar la fecha correcta
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() + userTimezoneOffset);
    
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    }).format(localDate);
  };

  // Función para formatear fecha y hora
  const formatDateTime = (dateString) => {
    if (!dateString) return 'No especificada';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Fecha inválida';
    
    return new Intl.DateTimeFormat('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  useEffect(() => {
    // Si estamos en modo creación, no necesitamos cargar datos
    if (isCreateMode) {
      setLoading(false);
      setShowEditForm(true);
      return;
    }
    
    const loadExitDetails = async () => {
      try {
        setLoading(true);
        console.log('🔄 Cargando detalles de salida ID:', id);

        // Cargar datos de la salida
        if (!id) {
          throw new Error('ID de salida no proporcionado');
        }
        
        const exitData = await exitsService.getById(id);
        console.log('📦 Datos de salida:', exitData);
        
        if (!exitData) {
          throw new Error('No se encontraron datos para esta salida');
        }
        
        setExit(exitData);

      } catch (error) {
        console.error('❌ Error cargando detalles de salida:', error);
        setError(error.message || 'Error al cargar los detalles de la salida');
      } finally {
        setLoading(false);
      }
    };

    if (id && id !== 'new') {
      loadExitDetails();
    } else if (!isCreateMode && (!id || id === '')) {
      setError('ID de salida no válido');
      setLoading(false);
    }
  }, [id, isCreateMode]);

  // Manejar tecla ESC para cerrar el visor de imágenes
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && showImageViewer) {
        setShowImageViewer(false);
      }
    };

    if (showImageViewer) {
      document.addEventListener('keydown', handleKeyPress);
      // Bloquear scroll del body cuando el modal está abierto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset';
    };
  }, [showImageViewer]);

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleSaveExit = async (exitData) => {
    // Si es nuevo, navegar a la lista de salidas
    if (isCreateMode) {
      navigate(`/exits?page=${page}`);
    } else {
      // Si es edición, actualizar los datos en pantalla
      setExit(exitData);
      setShowEditForm(false);
    }
  };

  const handleCancelEdit = () => {
    if (isCreateMode) {
      // Si estamos creando, volver a la lista
      navigate(`/exits?page=${page}`);
    } else {
      // Si estamos editando, cerrar el formulario
      setShowEditForm(false);
    }
  };

  const handleDelete = async () => {
    try {
      await exitsService.delete(id);
      navigate(`/exits?page=${page}`);
    } catch (error) {
      console.error('❌ Error eliminando salida:', error);
      setError(error.message);
    }
  };

  const handleGoBack = () => {
    navigate(`/exits?page=${page}`);
  };

  const handleImageUpload = async (imageFileName) => {
    try {
      setIsUploading(true);
      const updatedExit = await exitsService.updateImage(id, imageFileName);
      setExit(updatedExit);
      setShowImageUploader(false);
      console.log('✅ Imagen de requisición actualizada:', updatedExit);
    } catch (error) {
      console.error('❌ Error actualizando imagen de requisición:', error);
      setError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadImage = async () => {
    console.log('🔽 Intentando descargar imagen:', exit.requisition_image);
    
    if (exit.requisition_image) {
      try {
        const imageUrl = imagesService.getImageUrl(exit.requisition_image);
        console.log('📥 URL de descarga:', imageUrl);
        
        // Usar fetch para obtener la imagen como blob
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        // Crear URL temporal del blob
        const blobUrl = window.URL.createObjectURL(blob);
        
        // Crear link de descarga
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `requisicion_salida_${exit.id}_${exit.requisition_image}`;
        link.style.display = 'none';
        
        // Añadir al DOM, hacer clic y limpiar
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar URL temporal
        window.URL.revokeObjectURL(blobUrl);
        
        console.log('✅ Descarga iniciada correctamente');
      } catch (error) {
        console.error('❌ Error en descarga:', error);
        // Fallback: abrir en nueva pestaña si falla la descarga
        const imageUrl = imagesService.getImageUrl(exit.requisition_image);
        window.open(imageUrl, '_blank');
        console.log('⚠️ Fallback: abriendo imagen en nueva pestaña');
      }
    } else {
      console.warn('⚠️ No hay imagen para descargar');
    }
  };

  const handleViewImage = () => {
    console.log('👁️ Abriendo visor de imagen');
    setShowImageViewer(true);
  };

  const handleGenerateMaintenance = async () => {
    console.log('🔍 Datos de salida completos:', exit);
    console.log('🔍 Camión en salida:', exit?.truck);
    
    // Verificar si la salida tiene un camión asignado
    if (!exit?.truck || exit.truck.trim() === '') {
      alert('Esta salida no tiene un camión asignado.\n\nPara generar un mantenimiento, la salida debe tener un camión asociado.');
      return;
    }

    try {
      setLoadingTruck(true);
      console.log('🚚 Buscando camión:', exit.truck);
      
      // Buscar el camión por número económico
      const trucks = await trucksService.getAll({ search: exit.truck });
      console.log('📋 Resultado de búsqueda:', trucks);
      
      if (trucks && trucks.data && trucks.data.length > 0) {
        // Buscar coincidencia exacta con el número económico
        const truck = trucks.data.find(t => 
          t.numero_economico && 
          t.numero_economico.toLowerCase() === exit.truck.toLowerCase()
        );
        
        if (truck) {
          console.log('✅ Camión encontrado:', truck);
          setTruckData(truck);
          setShowMaintenanceModal(true);
        } else {
          console.warn('⚠️ No se encontró un camión con ese número económico');
          alert(`No se encontró el camión "${exit.truck}" en el sistema.\n\nVerifique que el número económico sea correcto o que el camión esté registrado en la flotilla.`);
        }
      } else {
        console.warn('⚠️ No se encontraron camiones');
        alert(`No se encontró el camión "${exit.truck}" en el sistema.\n\nAsegúrese de que el camión esté registrado en la flotilla.`);
      }
    } catch (error) {
      console.error('❌ Error cargando datos del camión:', error);
      alert('Error al cargar los datos del camión.\n\nPor favor, intente nuevamente o contacte al administrador.');
    } finally {
      setLoadingTruck(false);
    }
  };

  // Funciones para manejar fotos de entrega
  const handleDeliveryPhotoUpload = async (imageFileName) => {
    try {
      setIsUploading(true);
      
      // Obtener el array actual de fotos de entrega
      const currentPhotos = exit.delivery_photos || [];
      
      // Agregar la nueva foto
      const updatedPhotos = [...currentPhotos, imageFileName];
      
      // Actualizar en el backend
      const response = await exitsService.updateDeliveryPhotos(id, updatedPhotos);
      
      // Actualizar el estado local
      setExit(response);
      setShowDeliveryPhotosUploader(false);
      
      console.log('✅ Foto de entrega agregada:', imageFileName);
    } catch (error) {
      console.error('❌ Error agregando foto de entrega:', error);
      setError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveDeliveryPhoto = async (photoToRemove) => {
    try {
      const currentPhotos = exit.delivery_photos || [];
      const updatedPhotos = currentPhotos.filter(photo => photo !== photoToRemove);
      
      const response = await exitsService.updateDeliveryPhotos(id, updatedPhotos);
      setExit(response);
      
      console.log('✅ Foto de entrega eliminada:', photoToRemove);
    } catch (error) {
      console.error('❌ Error eliminando foto de entrega:', error);
      setError(error.message);
    }
  };

  const handleViewDeliveryPhoto = (photo) => {
    setSelectedDeliveryPhoto(photo);
  };

  const handleDownloadDeliveryPhoto = async (photo) => {
    try {
      const imageUrl = imagesService.getImageUrl(photo);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `entrega_salida_${exit.id}_${photo}`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
      
      console.log('✅ Foto de entrega descargada');
    } catch (error) {
      console.error('❌ Error descargando foto:', error);
      const imageUrl = imagesService.getImageUrl(photo);
      window.open(imageUrl, '_blank');
    }
  };

  // Si estamos cargando
  if (loading && !isCreateMode) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-gray-700 font-medium">Cargando detalles de la salida...</p>
        </div>
      </motion.div>
    );
  }

  // Si hay un error o no se encontró la salida
  if (error || (!exit && !isCreateMode)) {
    return (
      <motion.div
        className="min-h-[calc(100vh-180px)] px-4 py-8 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-700 mb-4">{error || 'Salida no encontrada'}</p>
          <button 
            onClick={handleGoBack}
            className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Volver a Salidas
          </button>
        </div>
      </motion.div>
    );
  }

  // Si estamos en modo creación, mostrar el formulario directamente
  if (isCreateMode && showEditForm) {
    return (
      <ExitFormMulti
        onSave={handleSaveExit}
        onCancel={handleCancelEdit}
      />
    );
  }

  // Si estamos en modo edición, mostrar el formulario
  if (showEditForm) {
    return (
      <ExitFormMulti
        initialData={exit}
        isEditMode={true}
        onSave={handleSaveExit}
        onCancel={handleCancelEdit}
      />
    );
  }

  return (
    <motion.div
      className="min-h-[calc(100vh-180px)] px-4 py-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={handleGoBack}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </motion.button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Detalle de Salida #{exit?.id || 'No disponible'}
            </h1>
            <p className="text-gray-600 mt-1">
              {exit?.product_name ? 
                `Producto principal: ${exit.product_name}` : 
                'Información completa de la salida del almacén'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <motion.button
            onClick={handleEdit}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Edit className="w-4 h-4" />
            Editar
          </motion.button>
          
          {/* Botón Generar Mantenimiento - siempre visible */}
          <motion.button
            onClick={handleGenerateMaintenance}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loadingTruck}
            title={exit?.truck ? `Generar mantenimiento para ${exit.truck}` : 'Esta salida no tiene camión asignado'}
          >
            <Wrench className="w-4 h-4" />
            {loadingTruck ? 'Cargando...' : 'Generar Mantenimiento'}
          </motion.button>
          {/* Botón Eliminar removido intencionalmente (solo UI) */}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Información Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Datos de la Salida */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Información de la Salida</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Hash className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">ID de Salida</p>
                    <p className="text-lg font-semibold text-gray-800">#{exit?.id || 'No disponible'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Producto Principal</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {exit.product_name || 'No especificado'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Package className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Total Productos</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {exit.products?.length || 0} productos diferentes
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Costo Total</p>
                    <p className="text-xl font-bold text-red-600">
                      {formatMoney(exit?.total_cost)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Fecha de Salida</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatDate(exit.exit_date)}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-500">Fecha de Registro</p>
                    <p className="text-lg font-semibold text-gray-800">
                      {formatDateTime(exit.created_at)}
                    </p>
                  </div>
                </div>

                {exit.requisition_number && (
                  <div className="flex items-start gap-3">
                    <Tag className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Número de Requisición</p>
                      <p className="text-lg font-semibold text-purple-600">
                        {exit.requisition_number}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Información del Solicitante y Detalles */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-100 rounded-lg">
                <User className="w-6 h-6 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Detalles de la Salida</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {exit.solicitante && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Solicitante</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.solicitante}</p>
                    </div>
                  </div>
                )}

                {exit.company && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Empresa</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.company}</p>
                    </div>
                  </div>
                )}

                {exit.delivered_to && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Entregado a</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.delivered_to}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {exit.truck && (
                  <div className="flex items-start gap-3">
                    <Truck className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Camión</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.truck}</p>
                    </div>
                  </div>
                )}

                {exit.workshop && (
                  <div className="flex items-start gap-3">
                    <Wrench className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Taller</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.workshop}</p>
                    </div>
                  </div>
                )}

                {exit.reason && (
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-gray-400 mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Motivo</p>
                      <p className="text-lg font-semibold text-gray-800">{exit.reason}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {exit.notes && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-500 mb-2">Notas</p>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {exit.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Lista de Productos */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">Productos Incluidos</h2>
            </div>

            {exit?.products && exit.products.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Producto
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cantidad
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Costo Unitario
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Costo Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {exit?.products.map((product, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              {product.product_image ? (
                                <img 
                                  src={product.product_image} 
                                  alt={product.product_name} 
                                  className="h-10 w-10 rounded-lg object-cover"
                                />
                              ) : (
                                <Package className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                              <div className="text-xs text-gray-500">{product.product_sku}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-900">
                            {product.quantity} {product.product_unit || 'pzs'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-900">
                            {formatMoney(product.unit_cost)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-gray-900">
                            {formatMoney(product.total_cost)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        Total
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                        {exit?.products.reduce((sum, p) => sum + p.quantity, 0)} unidades
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap"></td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-purple-700">
                        {formatMoney(exit?.total_cost)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Package className="w-12 h-12 text-gray-300 mx-auto" />
                <p className="mt-2 text-gray-500">No hay productos asociados a esta salida</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Resumen de la Salida */}
        <div className="space-y-6">
          {/* Imagen de Requisición */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-purple-600" />
                Imagen de Requisición
              </h3>
              <motion.button
                onClick={() => setShowImageUploader(true)}
                className="bg-purple-600 text-white p-2 rounded-full hover:bg-purple-700 transition-colors duration-300 shadow-lg"
                title="Subir/cambiar imagen de requisición"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Upload className="w-4 h-4" />
              </motion.button>
            </div>
            
            <div className="aspect-video bg-gray-100 rounded-xl overflow-hidden relative group border-2 border-dashed border-gray-300 cursor-pointer"
                 onClick={exit.requisition_image ? handleViewImage : () => setShowImageUploader(true)}>
              {exit.requisition_image ? (
                <img
                  src={imagesService.getImageUrl(exit.requisition_image)}
                  alt="Imagen de Requisición"
                  className="w-full h-full object-contain p-2"
                  onError={(e) => {
                    console.error('Error cargando imagen:', e);
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <ImageIcon className="w-12 h-12 mb-2" />
                  <p className="text-sm text-center">No hay imagen de requisición</p>
                  <p className="text-xs text-center mt-1">Haz clic para subir una</p>
                </div>
              )}
              
              {exit.requisition_image && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 flex items-center gap-3 transition-all duration-300">
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewImage();
                      }}
                      className="bg-blue-600 bg-opacity-95 text-white p-3 rounded-full transition-all duration-300 shadow-lg border-2 border-white"
                      title="Ver imagen en pantalla completa"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Eye className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadImage();
                      }}
                      className="bg-green-600 bg-opacity-95 text-white p-3 rounded-full transition-all duration-300 shadow-lg border-2 border-white"
                      title="Descargar imagen"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Download className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowImageUploader(true);
                      }}
                      className="bg-purple-600 bg-opacity-95 text-white p-3 rounded-full transition-all duration-300 shadow-lg border-2 border-white"
                      title="Cambiar imagen"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Upload className="w-5 h-5" />
                    </motion.button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Fotos de Entrega */}
          <motion.div
            className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                Fotos de Entrega
                {exit.delivery_photos && exit.delivery_photos.length > 0 && (
                  <span className="text-sm bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    {exit.delivery_photos.length}
                  </span>
                )}
              </h3>
              <motion.button
                onClick={() => setShowDeliveryPhotosUploader(true)}
                className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors duration-300 shadow-lg"
                title="Agregar foto de entrega"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Plus className="w-4 h-4" />
              </motion.button>
            </div>
            
            {exit.delivery_photos && exit.delivery_photos.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {exit.delivery_photos.map((photo, index) => (
                  <div
                    key={index}
                    className="aspect-video bg-gray-100 rounded-lg overflow-hidden relative group border border-gray-300 cursor-pointer"
                    onClick={() => handleViewDeliveryPhoto(photo)}
                  >
                    <img
                      src={imagesService.getImageUrl(photo)}
                      alt={`Foto de entrega ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        console.error('Error cargando foto:', e);
                        e.target.style.display = 'none';
                      }}
                    />
                    
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-2 transition-all duration-300">
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDeliveryPhoto(photo);
                          }}
                          className="bg-blue-600 bg-opacity-95 text-white p-2 rounded-full transition-all duration-300 shadow-lg"
                          title="Ver foto"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Eye className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadDeliveryPhoto(photo);
                          }}
                          className="bg-green-600 bg-opacity-95 text-white p-2 rounded-full transition-all duration-300 shadow-lg"
                          title="Descargar foto"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Download className="w-4 h-4" />
                        </motion.button>
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveDeliveryPhoto(photo);
                          }}
                          className="bg-red-600 bg-opacity-95 text-white p-2 rounded-full transition-all duration-300 shadow-lg"
                          title="Eliminar foto"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <X className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-300 cursor-pointer"
                   onClick={() => setShowDeliveryPhotosUploader(true)}>
                <ImageIcon className="w-12 h-12 mb-2" />
                <p className="text-sm text-center">No hay fotos de entrega</p>
                <p className="text-xs text-center mt-1">Haz clic para agregar</p>
              </div>
            )}
          </motion.div>

          <motion.div
            className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-lg p-6 border border-red-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-lg font-semibold text-red-800 mb-4">Resumen de la Salida</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-red-700">Productos Diferentes:</span>
                <span className="font-semibold text-red-800">
                  {exit.products?.length || 0} productos
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-red-700">Cantidad Total:</span>
                <span className="font-semibold text-red-800">
                  {exit.products?.reduce((sum, p) => sum + p.quantity, 0) || 0} unidades
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-red-700">Valor Total:</span>
                <span className="font-bold text-red-800 text-lg">
                  {formatMoney(exit?.total_cost)}
                </span>
              </div>
            </div>
          </motion.div>

          {/* Información Adicional */}
          {(exit.truck || exit.workshop || exit.company || exit.kilometraje !== undefined && exit.kilometraje !== null) && (
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Información Adicional</h3>
              
              <div className="space-y-3">
                {exit.truck && (
                  <div className="flex items-center gap-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-600">Camión:</span>
                    <span className="text-sm text-gray-800">{exit.truck}</span>
                  </div>
                )}
                
                {exit.workshop && (
                  <div className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-medium text-gray-600">Taller:</span>
                    <span className="text-sm text-gray-800">{exit.workshop}</span>
                  </div>
                )}
                
                {(exit.kilometraje !== undefined && exit.kilometraje !== null) && (
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-600">Kilometraje:</span>
                    <span className="text-sm text-gray-800">{Number(exit.kilometraje).toLocaleString('es-MX')} km</span>
                  </div>
                )}

                {exit.company && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-gray-600">Empresa:</span>
                    <span className="text-sm text-gray-800">{exit.company}</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Modal de Confirmación de Eliminación */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Confirmar Eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar esta salida? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de visualización de imagen */}
      <AnimatePresence>
        {showImageViewer && exit.requisition_image && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowImageViewer(false)}
          >
            <motion.div
              className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Botones de control */}
              <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
                <motion.button
                  onClick={handleDownloadImage}
                  className="bg-green-600 text-white p-3 rounded-full hover:bg-green-700 transition-colors duration-300 shadow-lg"
                  title="Descargar imagen"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={() => setShowImageViewer(false)}
                  className="bg-red-600 text-white p-3 rounded-full hover:bg-red-700 transition-colors duration-300 shadow-lg"
                  title="Cerrar"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Información de la imagen */}
              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white p-3 rounded-lg">
                <p className="text-sm font-medium">Requisición de Salida #{exit.id}</p>
                <p className="text-xs text-gray-300 mt-1">
                  {exit.requisition_number ? `Requisición: ${exit.requisition_number}` : 'Sin número de requisición'}
                </p>
              </div>

              {/* Imagen */}
              <img
                src={imagesService.getImageUrl(exit.requisition_image)}
                alt="Imagen de Requisición - Vista completa"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  console.error('Error cargando imagen:', e);
                  setShowImageViewer(false);
                }}
              />

              {/* Controles inferiores */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm">
                Haz clic fuera de la imagen o presiona ESC para cerrar
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de subida de imagen */}
      <AnimatePresence>
        {showImageUploader && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Subir Imagen de Requisición</h3>
              <ImageUploader
                onImageUpload={handleImageUpload}
                currentImage={exit.requisition_image}
                onCancel={() => setShowImageUploader(false)}
                type="requisition"
              />
              {isUploading && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Actualizando imagen...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de visor de fotos de entrega */}
      <AnimatePresence>
        {selectedDeliveryPhoto && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedDeliveryPhoto(null)}
          >
            <motion.div
              className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-4"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Botones de control */}
              <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
                <motion.button
                  onClick={() => handleDownloadDeliveryPhoto(selectedDeliveryPhoto)}
                  className="bg-green-600 text-white p-3 rounded-full hover:bg-green-700 transition-colors duration-300 shadow-lg"
                  title="Descargar foto"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="w-5 h-5" />
                </motion.button>
                <motion.button
                  onClick={() => setSelectedDeliveryPhoto(null)}
                  className="bg-red-600 text-white p-3 rounded-full hover:bg-red-700 transition-colors duration-300 shadow-lg"
                  title="Cerrar"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Información de la foto */}
              <div className="absolute top-4 left-4 bg-black bg-opacity-50 text-white p-3 rounded-lg">
                <p className="text-sm font-medium">Foto de Entrega - Salida #{exit.id}</p>
                <p className="text-xs text-gray-300 mt-1">
                  {exit.requisition_number ? `Requisición: ${exit.requisition_number}` : 'Sin número de requisición'}
                </p>
              </div>

              {/* Imagen */}
              <img
                src={imagesService.getImageUrl(selectedDeliveryPhoto)}
                alt="Foto de entrega - Vista completa"
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  console.error('Error cargando foto:', e);
                  setSelectedDeliveryPhoto(null);
                }}
              />

              {/* Controles inferiores */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-lg text-sm">
                Haz clic fuera de la imagen para cerrar
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de subida de fotos de entrega */}
      <AnimatePresence>
        {showDeliveryPhotosUploader && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Agregar Foto de Entrega</h3>
              <ImageUploader
                onImageUpload={handleDeliveryPhotoUpload}
                onCancel={() => setShowDeliveryPhotosUploader(false)}
                type="delivery"
              />
              {isUploading && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Subiendo foto...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de generación de mantenimiento */}
      <AnimatePresence>
        {showMaintenanceModal && truckData && (
          <MaintenanceModal 
            isOpen={showMaintenanceModal}
            onClose={(refresh) => {
              setShowMaintenanceModal(false);
              setTruckData(null);
              // Si se creó un mantenimiento y se solicitó actualizar
              if (refresh) {
                console.log('✅ Mantenimiento creado desde salida');
                // Opcionalmente, podrías recargar los datos de la salida aquí
              }
            }}
            truck={{
              ...truckData,
              // Asegurar que estos campos críticos existan
              id: truckData.id || null,
              serie: truckData.serie || '',
              km_actual: truckData.km_actual || 0
            }}
            exitData={{
              requisition_number: exit?.requisition_number || ''
            }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ExitDetailPage;
